import { useEffect, useRef, useState } from 'react';
import { LogoLockup } from '@/components/v2/logo';
import splashStyles from './splash.module.less';

/**
 * PWA launch splash - a designed launch moment, not a loading spinner.
 *
 * Rendered inside <body> by the root route, so the SSR byte stream paints the
 * wordmark before any JS runs, replacing the blank PWA cold-start screen. It
 * reuses the home-logo lockup markup (logo.module.less) as the brand moment.
 *
 * Lifecycle decisions (post-hydration only, so SSR and hydration markup always
 * match and no hydration warning can occur):
 * - Every cold load paints the splash markup - the server cannot know
 *   sessionStorage. The wordmark holds one designed beat while the CSS pulse
 *   loops, then the whole overlay fades out into the page underneath. The
 *   handoff is identical on every route: fade only, never a zoom/scale morph.
 * - prefers-reduced-motion: hand off instantly, no animation.
 * - sessionStorage marks the tab as splash-seen (best effort); SPA navigations
 *   never remount the splash, so it never re-shows mid-session.
 */
const SPLASH_SEEN_KEY = 'til-splash-seen';
// The designed hold counts from navigation start, so late hydration still
// shows the wordmark for the full beat only as long as the load itself took.
const DESIGN_HOLD_MS = 650;
const FADE_MS = 300;

export function PwaSplash() {
  const [mounted, setMounted] = useState(true);
  const overlayRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!mounted) return;
    const overlay = overlayRef.current;
    if (!overlay) return;

    const markSeen = () => {
      try {
        sessionStorage.setItem(SPLASH_SEEN_KEY, '1');
      } catch {
        // Private mode: session guard is best-effort only.
      }
    };

    // Failsafe: Animation.finished can stall on hidden tabs, and a stuck
    // splash would block the whole app.
    const playAndLeave = (anim: Animation) => {
      const leave = () => setMounted(false);
      const timer = setTimeout(leave, FADE_MS + 250);
      void anim.finished.then(
        () => {
          clearTimeout(timer);
          leave();
        },
        () => {
          clearTimeout(timer);
          leave();
        }
      );
    };

    // Reduced motion (or no WAAPI): instant swap instead of a fade.
    if (
      window.matchMedia('(prefers-reduced-motion: reduce)').matches ||
      typeof overlay.animate !== 'function'
    ) {
      markSeen();
      setMounted(false);
      return;
    }

    const fadeOut = () => {
      markSeen();
      playAndLeave(
        overlay.animate([{ opacity: 1 }, { opacity: 0 }], {
          duration: FADE_MS,
          easing: 'ease-in',
          fill: 'forwards',
        })
      );
    };

    const holdTimer = setTimeout(fadeOut, Math.max(0, DESIGN_HOLD_MS - performance.now()));
    return () => clearTimeout(holdTimer);
  }, [mounted]);

  if (!mounted) return null;

  return (
    // aria-hidden: the splash is decorative brand chrome; screen readers get
    // the real page content, not a duplicate lockup on every load.
    <div ref={overlayRef} className={splashStyles.overlay} aria-hidden="true">
      <div className={splashStyles.wordmark}>
        <LogoLockup />
      </div>
    </div>
  );
}
